/**
 * src/client/net/api.ts
 *
 * Fetch client for /api/scores.
 * - Persistent device token via crypto.randomUUID() in localStorage
 * - On submit failure: returns error, never loses score silently
 */

import type { ScoreSubmission, LeaderboardResponse, GetLeaderboardResponse } from "../../../shared/schema.js";

// ---------------------------------------------------------------------------
// Device token (persistent, never shown in UI)
// ---------------------------------------------------------------------------

const TOKEN_KEY = "neon_td_device_token";

export function getDeviceToken(): string {
  let token = localStorage.getItem(TOKEN_KEY);
  if (!token || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(token)) {
    token = crypto.randomUUID();
    localStorage.setItem(TOKEN_KEY, token);
  }
  return token;
}

// ---------------------------------------------------------------------------
// API responses
// ---------------------------------------------------------------------------

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; message: string };

// ---------------------------------------------------------------------------
// POST /api/scores
// ---------------------------------------------------------------------------

export interface SubmitScoreInput {
  nickname: string;
  score: number;
  wave: number;
  durationMs: number;
}

export async function submitScore(
  input: SubmitScoreInput,
): Promise<ApiResult<LeaderboardResponse>> {
  const token = getDeviceToken();

  const body: ScoreSubmission = {
    nickname: input.nickname,
    score: input.score,
    wave: input.wave,
    token,
    durationMs: input.durationMs,
  };

  let res: Response;
  try {
    res = await fetch("/api/scores", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, status: 0, message: "Network error — check your connection." };
  }

  if (!res.ok) {
    let message = `Server error (${res.status})`;
    try {
      const json = (await res.json()) as { error?: string; message?: string };
      message = json.error ?? json.message ?? message;
    } catch {
      // ignore parse error
    }
    return { ok: false, status: res.status, message };
  }

  try {
    const data = (await res.json()) as LeaderboardResponse;
    return { ok: true, data };
  } catch {
    return { ok: false, status: 200, message: "Invalid response from server." };
  }
}

// ---------------------------------------------------------------------------
// GET /api/scores
// ---------------------------------------------------------------------------

export async function getLeaderboard(
  week: "current" | "last" = "current",
): Promise<ApiResult<GetLeaderboardResponse>> {
  let res: Response;
  try {
    res = await fetch(`/api/scores?week=${week}`);
  } catch {
    return { ok: false, status: 0, message: "Network error." };
  }

  if (!res.ok) {
    return { ok: false, status: res.status, message: `Server error (${res.status})` };
  }

  try {
    const data = (await res.json()) as GetLeaderboardResponse;
    return { ok: true, data };
  } catch {
    return { ok: false, status: 200, message: "Invalid response from server." };
  }
}

// ---------------------------------------------------------------------------
// Personal best (localStorage)
// ---------------------------------------------------------------------------

const PB_KEY = "neon_td_personal_best";

export interface PersonalBest {
  score: number;
  wave: number;
}

export function getPersonalBest(): PersonalBest | null {
  try {
    const raw = localStorage.getItem(PB_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersonalBest;
  } catch {
    return null;
  }
}

export function maybeUpdatePersonalBest(score: number, wave: number): PersonalBest {
  const current = getPersonalBest();
  if (!current || score > current.score) {
    const pb = { score, wave };
    localStorage.setItem(PB_KEY, JSON.stringify(pb));
    return pb;
  }
  return current;
}
